import { useState, useEffect, useRef } from 'react';
import { ScoreProvider, useScore } from '../../context/ScoreContext';
import { MISSION_TASK_KEY } from '../../data/scheduleData';
import NavBar from './components/NavBar';
import HeroSection from './components/HeroSection';
import LiveScoreBoard from './components/LiveScoreBoard';
import PKBattleSection from './components/PKBattleSection';
import MemberRosterSection from './components/MemberRosterSection';
import ScoreSection from './components/ScoreSection';
import ScheduleSection from './components/ScheduleSection';
import FooterSection from './components/FooterSection';
import TeamSelectModal from './components/TeamSelectModal';
import ScoreControlPanel from './components/ScoreControlPanel';
import MemberProfileModal from './components/MemberProfileModal';
import StoryIntroModal from './components/StoryIntroModal';
import ScrollReveal from '../../components/feature/ScrollReveal';
import CommentModal from '../../components/feature/CommentModal';
import GameStartEffect from './components/GameStartEffect';
import SecretAngelEffect from './components/SecretAngelEffect';
import { useComments } from '../../hooks/useComments';
import LotteryModal from './components/LotteryModal';
import SecretAngelRevealCeremony from './components/SecretAngelRevealCeremony';
import RatingModal from './components/RatingModal';
import MemoryWallModal from './components/MemoryWallModal';
import JoinCelebration from './components/JoinCelebration';
import NotificationToast from '../../components/feature/NotificationToast';
import SettlementOverlay from './components/SettlementOverlay';
import WinnerAnnouncementOverlay from './components/WinnerAnnouncementOverlay';
import RatingResultOverlay from './components/RatingResultOverlay';
import HiddenMissionOverlay from './components/HiddenMissionOverlay';
import { useWinnerBroadcast } from '../../hooks/useWinnerBroadcast';
import { useRatingResultBroadcast } from '../../hooks/useRatingResultBroadcast';

const STORY_KEY = 'srs_story_v1';

function LoadingOverlay() {
  const { isLoading } = useScore();
  if (!isLoading) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-[#0d0d0d] flex flex-col items-center justify-center">
      <div className="w-12 h-12 rounded-full border-2 border-[#d4a843]/30 border-t-[#d4a843] animate-spin mb-5" />
      <p className="text-[#d4a843] text-xs tracking-[0.4em] uppercase">Loading</p>
    </div>
  );
}

function FloatingButtons() {
  const { addComment } = useComments();
  const [showCommentModal, setShowCommentModal] = useState(false);

  return (
    <>
      {/* ── 我要說話 — 右下角，黃色，上下浮動 ── */}
      <div
        className="fixed bottom-5 right-3 sm:bottom-8 sm:right-8 z-40"
        style={{ animation: 'speakFloat 3s ease-in-out infinite' }}
      >
        {/* 紅色霓虹外框 */}
        <div className="absolute -inset-[3px] rounded-full pointer-events-none"
          style={{
            background: 'transparent',
            border: '1.5px solid rgba(255,60,60,0.35)',
            boxShadow: '0 0 6px rgba(255,40,40,0.4), 0 0 12px rgba(255,40,40,0.2), 0 0 22px rgba(255,40,40,0.1)',
            animation: 'neonFlicker 3.5s ease-in-out infinite',
          }}
        />
        <button
          onClick={() => setShowCommentModal(true)}
          className="relative flex items-center gap-2 px-4 py-2.5 sm:px-5 sm:py-3 rounded-full cursor-pointer transition-all duration-200 active:scale-95 whitespace-nowrap"
          style={{
            background: 'linear-gradient(135deg, #1e1408 0%, #130e03 100%)',
            border: '1.5px solid rgba(212,168,67,0.55)',
            boxShadow: '0 0 18px rgba(212,168,67,0.35), 0 4px 20px rgba(0,0,0,0.5)',
          }}
        >
          <span className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-megaphone-fill text-base sm:text-lg"
              style={{ color: '#d4a843', filter: 'drop-shadow(0 0 5px rgba(212,168,67,0.8))' }} />
          </span>
          <span className="text-xs sm:text-sm font-black tracking-wider"
            style={{ color: '#d4a843', fontFamily: "'Orbitron', monospace", textShadow: '0 0 8px rgba(212,168,67,0.6)' }}
          >
            說話
          </span>
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d4a843] opacity-60" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#d4a843]" />
          </span>
        </button>
      </div>

      {showCommentModal && (
        <CommentModal
          onClose={() => setShowCommentModal(false)}
          addComment={addComment}
        />
      )}

      <style>{`
        @keyframes speakFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-6px); }
        }
        @keyframes neonFlicker {
          0%, 100% { opacity: 1; }
          45%       { opacity: 1; }
          47%       { opacity: 0.6; }
          49%       { opacity: 1; }
          70%       { opacity: 1; }
          72%       { opacity: 0.4; }
          74%       { opacity: 1; }
        }
      `}</style>
    </>
  );
}

function HomeContent() {
  const { setShowTeamModal, selectedTeam, currentMember, isLoading, scheduleState, setCeremonyActive, showJudgePanel, setShowJudgePanel, setShowMemberModal } = useScore();
  const { ceremonyActive, unlockedTasks } = scheduleState;
  const { announcement, dismissAnnouncement } = useWinnerBroadcast();
  const { pendingResult, clearResult } = useRatingResultBroadcast();
  const [showStory, setShowStory] = useState(
    () => !sessionStorage.getItem(STORY_KEY),
  );
  const [showLottery, setShowLottery] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [showMemoryWall, setShowMemoryWall] = useState(false);
  const [localDismissed, setLocalDismissed] = useState(false);
  const [joiningTeam, setJoiningTeam] = useState<'black' | 'white' | null>(null);

  // Listen for join celebration event (fired from PledgeModal after confirming)
  useEffect(() => {
    const handler = (e: Event) => {
      const team = (e as CustomEvent<{ team: 'black' | 'white' }>).detail?.team ?? null;
      setJoiningTeam(team);
    };
    window.addEventListener('srs-join-celebration', handler);
    return () => window.removeEventListener('srs-join-celebration', handler);
  }, []);

  // 遊戲重置後，讓故事重新出現（讓玩家重新加入）
  useEffect(() => {
    const handler = () => {
      // 延遲一下讓 selectedTeam state 先更新
      setTimeout(() => {
        if (!sessionStorage.getItem(STORY_KEY)) {
          setShowStory(true);
        }
      }, 100);
    };
    window.addEventListener('srs-rewards-reset', handler);
    return () => window.removeEventListener('srs-rewards-reset', handler);
  }, []);

  // 裁判身份：一旦成功開啟裁判面板（代表已輸入正確 PIN），本裝置本次 session 就維持裁判身份
  const [judgeUnlocked, setJudgeUnlocked] = useState(false);
  useEffect(() => {
    if (showJudgePanel) setJudgeUnlocked(true);
  }, [showJudgePanel]);

  // 通知卡點擊 → 開啟回憶牆
  useEffect(() => {
    const handler = () => setShowMemoryWall(true);
    window.addEventListener('srs-open-memory-wall', handler);
    return () => window.removeEventListener('srs-open-memory-wall', handler);
  }, []);

  // 當 ceremonyActive 從 false 變 true → 重置本地關閉狀態（新一輪揭曉）
  const prevCeremonyActiveRef = useState<boolean>(false);
  useEffect(() => {
    if (ceremonyActive && !prevCeremonyActiveRef[0]) {
      setLocalDismissed(false);
    }
    prevCeremonyActiveRef[1](ceremonyActive);
  }, [ceremonyActive]);

  const handleCeremonyClose = () => {
    setLocalDismissed(true);
  };

  useEffect(() => {
    if (isLoading) return;
    if (selectedTeam && currentMember) {
      setShowStory(false);
      sessionStorage.setItem(STORY_KEY, '1');
    } else if (sessionStorage.getItem(STORY_KEY) && !selectedTeam) {
      setShowTeamModal(true);
    } else if (!selectedTeam && !sessionStorage.getItem(STORY_KEY)) {
      // 沒有隊伍也沒有故事紀錄 → 重新顯示開場（處理重置後的情況）
      setShowStory(true);
    }
  }, [isLoading, selectedTeam, currentMember, setShowTeamModal]);

  const handleStoryConfirm = () => {
    sessionStorage.setItem(STORY_KEY, '1');
    setShowStory(false);
    setShowTeamModal(true);
  };

  // ── 小主人任務觸發時，自動彈出個人資料頁讓玩家即時看到小主人 ────────────
  const prevMissionRef = useRef(false);
  const missionReadyRef = useRef(false);
  useEffect(() => {
    // 等資料載入完畢後才開始偵測
    if (isLoading) return;
    const hasMission = !!unlockedTasks[MISSION_TASK_KEY];
    if (!missionReadyRef.current) {
      // 首次載入：靜默初始化，不彈出 modal（避免重整後立刻跳出）
      prevMissionRef.current = hasMission;
      missionReadyRef.current = true;
      return;
    }
    // 僅在任務「從未解鎖 → 解鎖」時才觸發
    if (!prevMissionRef.current && hasMission && currentMember) {
      setShowMemberModal(true);
    }
    prevMissionRef.current = hasMission;
  }, [isLoading, unlockedTasks, currentMember, setShowMemberModal]);

  return (
    <>
      {showStory && <StoryIntroModal onConfirm={handleStoryConfirm} />}
      <LoadingOverlay />
      <GameStartEffect />
      <SecretAngelEffect />
      <TeamSelectModal />
      <MemberProfileModal />
      <ScoreControlPanel />
      <NavBar
        onOpenLottery={() => setShowLottery(true)}
        onOpenRating={() => setShowRating(true)}
        onOpenMemoryWall={() => setShowMemoryWall(true)}
      />
      {showLottery && <LotteryModal onClose={() => setShowLottery(false)} />}
      {showRating && <RatingModal onClose={() => setShowRating(false)} />}
      {showMemoryWall && (
        <MemoryWallModal
          onClose={() => setShowMemoryWall(false)}
          isJudge={judgeUnlocked}
        />
      )}

      {/* Join celebration — rendered above MemberProfileModal (z-90) */}
      {joiningTeam !== null && (
        <JoinCelebration
          team={joiningTeam}
          onDone={() => setJoiningTeam(null)}
        />
      )}

      {/* 全域揭曉儀式 */}
      {ceremonyActive && !localDismissed && (
        <SecretAngelRevealCeremony
          isJudge={judgeUnlocked}
          onClose={handleCeremonyClose}
          onOpenJudgePanel={() => setShowJudgePanel(true)}
        />
      )}

      {/* 得獎廣播覆蓋層 */}
      {announcement && (
        <WinnerAnnouncementOverlay
          announcement={announcement}
          onDismiss={dismissAnnouncement}
          isJudge={judgeUnlocked}
        />
      )}

      {/* 評分結果公告覆蓋層 */}
      {pendingResult && (
        <RatingResultOverlay
          result={pendingResult}
          onDismiss={clearResult}
          isJudge={judgeUnlocked}
        />
      )}

      {/* 結算成績覆蓋層 */}
      <SettlementOverlay />

      {/* 即時系統通知 — 加入戰局 / 戰場留言 */}
      <NotificationToast />

      <FloatingButtons />

      <HeroSection />
      <ScrollReveal direction="up" distance={40}>
        <LiveScoreBoard />
      </ScrollReveal>
      <ScrollReveal direction="up" distance={48} delay={60}>
        <PKBattleSection />
      </ScrollReveal>
      <ScrollReveal direction="up" distance={36}>
        <MemberRosterSection />
      </ScrollReveal>
      <ScrollReveal direction="up" distance={44} delay={40}>
        <ScheduleSection />
      </ScrollReveal>
      <ScrollReveal direction="right" distance={40}>
        <ScoreSection />
      </ScrollReveal>
      <ScrollReveal direction="up" distance={20} threshold={0.03}>
        <FooterSection />
      </ScrollReveal>
      <HiddenMissionOverlay />
    </>
  );
}

export default function Home() {
  return (
    <ScoreProvider>
      <div className="min-h-screen bg-[#06060f]">
        <HomeContent />
      </div>
    </ScoreProvider>
  );
}
